// ============================================================
// daysUntilLocal.ts
// Shared local-midnight day-diff helper.
//
// Deadline timestamps arrive as UTC-midnight ISO strings from
// the backend (case_deadlines and date-only tasks are stored as
// midnight UTC — see calendarController.ts L88-93 rationale in
// CalendarView.tsx). Comparing a UTC-midnight timestamp against
// a raw `Date.now()` produces the classic BST/CEST off-by-one:
// a "tomorrow local" deadline computes ~0.4 days and Math.round
// drops it to 0 → the dashboard mislabels it as "Today".
//
// Mirrors the calendar's own approach at CalendarView.tsx
// L129-137 verbatim: normalise BOTH anchors to LOCAL midnight
// via `setHours(0, 0, 0, 0)`, THEN diff in whole days. The
// calendar's docblock at L110-113 explicitly flags the raw-
// millisecond pattern as the wrong thing to do ("Buckets
// entries into ordered groups based on the diff in DAYS (not
// milliseconds — that loses to DST and timezone bumps) between
// the entry's date and TODAY-AT-LOCAL-MIDNIGHT.").
//
// Returns Math.round of the local-day diff, so the semantics
// match the calendar's bucketing exactly:
//   -N …  → N days ago
//    0    → today
//   +1    → tomorrow
//   +N    → in N days
//
// Invalid ISO strings: `new Date(x).setHours(...)` on an invalid
// Date is still an invalid Date (getTime returns NaN); the
// arithmetic below yields NaN, and Math.round(NaN) → NaN. Callers
// should decide how to render NaN, but this matches the calendar's
// implicit behaviour (it doesn't guard invalid dates either —
// they'd fall through to the ">= 8 → month-year" bucket via
// toLocaleDateString on an invalid Date, which is not our concern).
// ============================================================
export function daysUntilLocal(iso: string): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(iso)
  target.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - today.getTime()) / 86400000)
}
